import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { empresasApi, seriesApi, plantillasApi, pushApi, exportApi } from '../lib/api';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/utils';
import { 
  Building2, 
  Users, 
  CreditCard,
  Save,
  Trash2,
  Plus,
  Crown,
  Hash,
  Pencil,
  FileText,
  Clock,
  Bell,
  Send,
  Download,
  BellRing,
  CheckCircle,
  XCircle
} from 'lucide-react';

export default function ConfiguracionPage() {
  const { empresaActual, hasPermission, loadUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [empresa, setEmpresa] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [series, setSeries] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [limites, setLimites] = useState(null);
  const [nuevoUsuarioEmail, setNuevoUsuarioEmail] = useState('');
  const [nuevoUsuarioRol, setNuevoUsuarioRol] = useState('VIEWER');
  
  // Serie dialog
  const [serieDialogOpen, setSerieDialogOpen] = useState(false);
  const [editingSerie, setEditingSerie] = useState(null);
  const [serieForm, setSerieForm] = useState({
    codigo: '',
    nombre: '',
    prefijo: '',
    activa: true
  });
  
  // Plantilla dialog
  const [plantillaDialogOpen, setPlantillaDialogOpen] = useState(false);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [plantillaForm, setPlantillaForm] = useState({
    nombre: '',
    descripcion: '',
    items: [{ descripcion: '', cantidad: 1, precio_unitario: 0 }],
    validez_dias: 30,
    forma_pago: 'Transferencia bancaria',
    notas: ''
  });
  
  // Recordatorios state
  const [recordatoriosConfig, setRecordatoriosConfig] = useState({
    dias_antes_vencimiento: 3,
    enviar_recordatorio: true
  });
  const [enviandoRecordatorios, setEnviandoRecordatorios] = useState(false);
  
  // Push notifications state
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  
  // Export state
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    estado: '',
    fecha_desde: '',
    fecha_hasta: ''
  });

  useEffect(() => {
    const loadData = async () => {
      if (!empresaActual?.id) return;
      
      try {
        const [empresaRes, usuariosRes, seriesRes, plantillasRes, limitesRes, recordatoriosRes] = await Promise.all([
          empresasApi.getOne(empresaActual.id),
          empresasApi.getUsuarios(empresaActual.id).catch(() => ({ data: [] })),
          seriesApi.getAll(empresaActual.id).catch(() => ({ data: [] })),
          plantillasApi.getAll(empresaActual.id).catch(() => ({ data: [] })),
          empresasApi.getLimites(empresaActual.id).catch(() => ({ data: null })),
          empresasApi.getRecordatoriosConfig(empresaActual.id).catch(() => ({ data: { dias_antes_vencimiento: 3, enviar_recordatorio: true } }))
        ]);
        setEmpresa(empresaRes.data);
        setUsuarios(usuariosRes.data);
        setSeries(seriesRes.data);
        setPlantillas(plantillasRes.data || []);
        setLimites(limitesRes.data);
        setRecordatoriosConfig(recordatoriosRes.data);
      } catch (error) {
        console.error('Error loading config:', error);
        toast.error('Error al cargar la configuración');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [empresaActual?.id]);

  const handleSaveEmpresa = async () => {
    setSaving(true);
    try {
      await empresasApi.update(empresaActual.id, empresa);
      toast.success('Configuración guardada');
      loadUser();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleAddUsuario = async () => {
    if (!nuevoUsuarioEmail) {
      toast.error('Introduce un email');
      return;
    }
    
    try {
      await empresasApi.addUsuario(empresaActual.id, nuevoUsuarioEmail, nuevoUsuarioRol);
      toast.success('Usuario añadido');
      setNuevoUsuarioEmail('');
      const res = await empresasApi.getUsuarios(empresaActual.id);
      setUsuarios(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir usuario');
    }
  };

  const handleRemoveUsuario = async (usuarioId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    
    try {
      await empresasApi.removeUsuario(empresaActual.id, usuarioId);
      toast.success('Usuario eliminado');
      setUsuarios(usuarios.filter(u => u.id !== usuarioId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Series handlers
  const openSerieDialog = (serie = null) => {
    if (serie) {
      setEditingSerie(serie);
      setSerieForm({
        codigo: serie.codigo,
        nombre: serie.nombre,
        prefijo: serie.prefijo,
        activa: serie.activa
      });
    } else {
      setEditingSerie(null);
      const year = new Date().getFullYear();
      setSerieForm({
        codigo: '',
        nombre: '',
        prefijo: '',
        activa: true
      });
    }
    setSerieDialogOpen(true);
  };

  const handleSaveSerie = async () => {
    if (!serieForm.codigo || !serieForm.nombre) {
      toast.error('Código y nombre son obligatorios');
      return;
    }

    try {
      const year = new Date().getFullYear();
      const prefijo = serieForm.prefijo || `${serieForm.codigo.toUpperCase()}-${year}`;
      
      if (editingSerie) {
        await seriesApi.update(editingSerie.id, { ...serieForm, prefijo });
        toast.success('Serie actualizada');
      } else {
        await seriesApi.create({
          ...serieForm,
          prefijo,
          empresa_id: empresaActual.id
        });
        toast.success('Serie creada');
      }
      
      setSerieDialogOpen(false);
      const res = await seriesApi.getAll(empresaActual.id);
      setSeries(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar serie');
    }
  };

  const handleDeleteSerie = async (serieId) => {
    if (!window.confirm('¿Eliminar esta serie?')) return;
    
    try {
      await seriesApi.delete(serieId);
      toast.success('Serie eliminada');
      setSeries(series.filter(s => s.id !== serieId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Plantillas
  const openPlantillaDialog = (plantilla = null) => {
    if (plantilla) {
      setEditingPlantilla(plantilla);
      setPlantillaForm({
        nombre: plantilla.nombre,
        descripcion: plantilla.descripcion || '',
        items: plantilla.items?.length > 0 ? plantilla.items : [{ descripcion: '', cantidad: 1, precio_unitario: 0 }],
        validez_dias: plantilla.validez_dias || 30,
        forma_pago: plantilla.forma_pago || 'Transferencia bancaria',
        notas: plantilla.notas || ''
      });
    } else {
      setEditingPlantilla(null);
      setPlantillaForm({
        nombre: '',
        descripcion: '',
        items: [{ descripcion: '', cantidad: 1, precio_unitario: 0 }],
        validez_dias: 30,
        forma_pago: 'Transferencia bancaria',
        notas: ''
      });
    }
    setPlantillaDialogOpen(true);
  };

  const handleSavePlantilla = async () => {
    if (!plantillaForm.nombre) {
      toast.error('El nombre es obligatorio');
      return;
    }

    try {
      if (editingPlantilla) {
        await plantillasApi.update(editingPlantilla.id, plantillaForm);
        toast.success('Plantilla actualizada');
      } else {
        await plantillasApi.create({
          ...plantillaForm,
          empresa_id: empresaActual.id
        });
        toast.success('Plantilla creada');
      }
      
      setPlantillaDialogOpen(false);
      const res = await plantillasApi.getAll(empresaActual.id);
      setPlantillas(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar plantilla');
    }
  };

  const handleDeletePlantilla = async (plantillaId) => {
    if (!window.confirm('¿Eliminar esta plantilla?')) return;
    
    try {
      await plantillasApi.delete(plantillaId);
      toast.success('Plantilla eliminada');
      setPlantillas(plantillas.filter(p => p.id !== plantillaId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const addPlantillaItem = () => {
    setPlantillaForm({
      ...plantillaForm,
      items: [...plantillaForm.items, { descripcion: '', cantidad: 1, precio_unitario: 0 }]
    });
  };

  const removePlantillaItem = (index) => {
    if (plantillaForm.items.length > 1) {
      const newItems = [...plantillaForm.items];
      newItems.splice(index, 1);
      setPlantillaForm({ ...plantillaForm, items: newItems });
    }
  };

  const updatePlantillaItem = (index, field, value) => {
    const newItems = [...plantillaForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setPlantillaForm({ ...plantillaForm, items: newItems });
  };

  // Recordatorios
  const handleSaveRecordatoriosConfig = async () => {
    try {
      await empresasApi.updateRecordatoriosConfig(empresaActual.id, recordatoriosConfig);
      toast.success('Configuración de recordatorios guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const handleEnviarRecordatorios = async () => {
    setEnviandoRecordatorios(true);
    try {
      const response = await empresasApi.enviarRecordatorios(empresaActual.id);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar recordatorios');
    } finally {
      setEnviandoRecordatorios(false);
    }
  };

  // Push Notifications handlers
  const checkPushSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setPushSubscribed(!!subscription);
    } catch (error) {
      console.log('Push not supported');
    }
  };

  const handleSubscribePush = async () => {
    setPushLoading(true);
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Necesitas permitir las notificaciones');
        return;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array('BD0O5aU8LZlsoic7GormAHp9KYXv1XjBuG7HcrBlOUC4cgSxhsev0j4vGNtYdJN6ZUpO6pFBJ95dc2f7LNUsMR0')
      });

      // Send to server
      await pushApi.subscribe(subscription.toJSON());
      setPushSubscribed(true);
      toast.success('Notificaciones activadas');
    } catch (error) {
      console.error('Push subscription error:', error);
      toast.error('Error al activar notificaciones');
    } finally {
      setPushLoading(false);
    }
  };

  const handleUnsubscribePush = async () => {
    setPushLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await pushApi.unsubscribe(subscription.endpoint);
      }
      
      setPushSubscribed(false);
      toast.success('Notificaciones desactivadas');
    } catch (error) {
      toast.error('Error al desactivar notificaciones');
    } finally {
      setPushLoading(false);
    }
  };

  const handleSendTestPush = async () => {
    try {
      await pushApi.sendTest();
      toast.success('Notificación de prueba enviada');
    } catch (error) {
      toast.error('Error al enviar notificación de prueba');
    }
  };

  // Export handlers
  const handleExportPresupuestos = async () => {
    setExportLoading(true);
    try {
      const response = await exportApi.presupuestos(empresaActual.id, exportFilters);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `presupuestos_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Exportación completada');
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('Exportar a CSV requiere plan Pro o superior');
      } else {
        toast.error('Error al exportar');
      }
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportClientes = async () => {
    setExportLoading(true);
    try {
      const response = await exportApi.clientes(empresaActual.id);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clientes_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Exportación completada');
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('Exportar a CSV requiere plan Pro o superior');
      } else {
        toast.error('Error al exportar');
      }
    } finally {
      setExportLoading(false);
    }
  };

  // Helper function for VAPID key
  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Check push subscription on mount
  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      checkPushSubscription();
    }
  }, []);

  const isOwner = hasPermission(['OWNER']);
  const canEdit = hasPermission(['OWNER', 'MANAGER']);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!empresa) return null;

  const planInfo = {
    FREE: { name: 'Starter', color: 'bg-slate-100 text-slate-700' },
    PRO: { name: 'Profesional', color: 'bg-blue-100 text-blue-700' },
    BUSINESS: { name: 'Equipos', color: 'bg-violet-100 text-violet-700' }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="configuracion-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Configuración
          </h1>
          <p className="text-slate-500 mt-1">
            Gestiona la configuración de tu empresa
          </p>
        </div>

        <Tabs defaultValue="empresa" className="space-y-6">
          <TabsList className="flex-wrap">
            <TabsTrigger value="empresa" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Empresa</span>
            </TabsTrigger>
            {isOwner && (
              <TabsTrigger value="series" className="flex items-center gap-2">
                <Hash className="w-4 h-4" />
                <span className="hidden sm:inline">Series</span>
              </TabsTrigger>
            )}
            {canEdit && (
              <TabsTrigger value="plantillas" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Plantillas</span>
              </TabsTrigger>
            )}
            {isOwner && (
              <TabsTrigger value="usuarios" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Usuarios</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="plan" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Plan</span>
            </TabsTrigger>
            {isOwner && (
              <TabsTrigger value="recordatorios" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span className="hidden sm:inline">Recordatorios</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="notificaciones" className="flex items-center gap-2">
              <BellRing className="w-4 h-4" />
              <span className="hidden sm:inline">Notificaciones</span>
            </TabsTrigger>
            {canEdit && empresa?.plan !== 'FREE' && (
              <TabsTrigger value="exportar" className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Exportar</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Empresa Tab */}
          <TabsContent value="empresa">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Datos de la Empresa</CardTitle>
                <CardDescription>
                  Esta información aparecerá en tus presupuestos y PDFs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="nombre_comercial">Nombre Comercial *</Label>
                    <Input
                      id="nombre_comercial"
                      value={empresa.nombre_comercial || ''}
                      onChange={(e) => setEmpresa({ ...empresa, nombre_comercial: e.target.value })}
                      disabled={!isOwner}
                      data-testid="config-nombre-comercial"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="razon_social">Razón Social</Label>
                    <Input
                      id="razon_social"
                      value={empresa.razon_social || ''}
                      onChange={(e) => setEmpresa({ ...empresa, razon_social: e.target.value })}
                      disabled={!isOwner}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cif_nif">CIF/NIF</Label>
                    <Input
                      id="cif_nif"
                      value={empresa.cif_nif || ''}
                      onChange={(e) => setEmpresa({ ...empresa, cif_nif: e.target.value })}
                      disabled={!isOwner}
                      data-testid="config-cif"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telefono">Teléfono</Label>
                    <Input
                      id="telefono"
                      value={empresa.telefono || ''}
                      onChange={(e) => setEmpresa({ ...empresa, telefono: e.target.value })}
                      disabled={!isOwner}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="direccion">Dirección</Label>
                  <Input
                    id="direccion"
                    value={empresa.direccion || ''}
                    onChange={(e) => setEmpresa({ ...empresa, direccion: e.target.value })}
                    disabled={!isOwner}
                    data-testid="config-direccion"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Logo de la Empresa</Label>
                  <div className="flex items-start gap-4">
                    {empresa.logo ? (
                      <div className="relative">
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/logos/${empresa.logo}`}
                          alt="Logo"
                          className="w-24 h-24 object-contain border rounded-lg bg-white"
                        />
                        {isOwner && (
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-6 w-6"
                            onClick={async () => {
                              try {
                                await empresasApi.deleteLogo(empresaActual.id);
                                setEmpresa({ ...empresa, logo: null });
                                toast.success('Logo eliminado');
                              } catch (error) {
                                toast.error('Error al eliminar logo');
                              }
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center text-slate-400">
                        <Building2 className="w-8 h-8" />
                      </div>
                    )}
                    {isOwner && (
                      <div className="flex-1">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            if (file.size > 5 * 1024 * 1024) {
                              toast.error('El archivo es muy grande. Máximo 5MB');
                              return;
                            }
                            
                            try {
                              const response = await empresasApi.uploadLogo(empresaActual.id, file);
                              setEmpresa({ ...empresa, logo: response.data.logo });
                              toast.success('Logo subido correctamente');
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Error al subir logo');
                            }
                          }}
                          className="cursor-pointer"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          JPG, PNG, GIF, WebP o SVG. Máximo 5MB.
                        </p>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">El logo aparecerá en los PDFs de presupuestos</p>
                </div>

                {/* Email del remitente personalizado */}
                <div className="space-y-4 pt-4 border-t border-slate-100">
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-1">Configuración de Email</h4>
                    <p className="text-xs text-slate-500">Personaliza el remitente de los emails enviados a tus clientes</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email_remitente_nombre">Nombre del Remitente</Label>
                      <Input
                        id="email_remitente_nombre"
                        value={empresa.email_remitente_nombre || ''}
                        onChange={(e) => setEmpresa({ ...empresa, email_remitente_nombre: e.target.value })}
                        placeholder={empresa.nombre_comercial || 'Tu Empresa'}
                        disabled={!isOwner}
                        data-testid="config-email-nombre"
                      />
                      <p className="text-xs text-slate-500">
                        Por defecto: {empresa.nombre_comercial || 'Nombre comercial'}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email_remitente_email">Email para Respuestas (Reply-To)</Label>
                      <Input
                        id="email_remitente_email"
                        type="email"
                        value={empresa.email_remitente_email || ''}
                        onChange={(e) => setEmpresa({ ...empresa, email_remitente_email: e.target.value })}
                        placeholder="presupuestos@tudominio.com"
                        disabled={!isOwner}
                        data-testid="config-email-remitente"
                      />
                      <p className="text-xs text-slate-500">
                        Las respuestas de los clientes llegarán a este email
                      </p>
                    </div>
                  </div>
                </div>

                {/* Colores de comunicaciones */}
                <div className="space-y-4 pt-4 border-t border-slate-100">
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-1">Colores de Comunicaciones</h4>
                    <p className="text-xs text-slate-500">Personaliza los colores de los emails enviados a clientes</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="color_primario">Color Primario (Botones)</Label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          id="color_primario"
                          value={empresa.color_primario || '#2563EB'}
                          onChange={(e) => setEmpresa({ ...empresa, color_primario: e.target.value })}
                          disabled={!isOwner}
                          className="w-12 h-10 rounded border cursor-pointer disabled:opacity-50"
                          data-testid="config-color-primario"
                        />
                        <Input
                          value={empresa.color_primario || '#2563EB'}
                          onChange={(e) => setEmpresa({ ...empresa, color_primario: e.target.value })}
                          placeholder="#2563EB"
                          disabled={!isOwner}
                          className="flex-1 font-mono text-sm"
                        />
                      </div>
                      <p className="text-xs text-slate-500">Se usa en botones y enlaces</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="color_secundario">Color Secundario (Títulos)</Label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          id="color_secundario"
                          value={empresa.color_secundario || '#0F172A'}
                          onChange={(e) => setEmpresa({ ...empresa, color_secundario: e.target.value })}
                          disabled={!isOwner}
                          className="w-12 h-10 rounded border cursor-pointer disabled:opacity-50"
                          data-testid="config-color-secundario"
                        />
                        <Input
                          value={empresa.color_secundario || '#0F172A'}
                          onChange={(e) => setEmpresa({ ...empresa, color_secundario: e.target.value })}
                          placeholder="#0F172A"
                          disabled={!isOwner}
                          className="flex-1 font-mono text-sm"
                        />
                      </div>
                      <p className="text-xs text-slate-500">Se usa en títulos y encabezados</p>
                    </div>
                  </div>
                  {/* Preview */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-2">Vista previa:</p>
                    <div className="bg-white p-4 rounded border">
                      <h3 style={{ color: empresa.color_secundario || '#0F172A' }} className="font-bold mb-2">Nuevo Presupuesto</h3>
                      <p className="text-sm text-slate-600 mb-3">Ejemplo de contenido del email...</p>
                      <button 
                        style={{ backgroundColor: empresa.color_primario || '#2563EB' }}
                        className="px-4 py-2 text-white text-sm rounded"
                      >
                        Ver Presupuesto
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="terminos">Términos y Condiciones por Defecto</Label>
                  <Textarea
                    id="terminos"
                    value={empresa.terminos_condiciones || ''}
                    onChange={(e) => setEmpresa({ ...empresa, terminos_condiciones: e.target.value })}
                    rows={4}
                    disabled={!isOwner}
                    data-testid="config-terminos"
                  />
                  <p className="text-xs text-slate-500">
                    Estos términos se incluirán en todos tus presupuestos
                  </p>
                </div>

                {isOwner && (
                  <Button 
                    onClick={handleSaveEmpresa} 
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={saving}
                    data-testid="config-save-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Series Tab */}
          {isOwner && (
            <TabsContent value="series">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Series de Numeración</CardTitle>
                      <CardDescription>
                        Configura las series para organizar tus presupuestos
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={() => openSerieDialog()} 
                      className="bg-blue-600 hover:bg-blue-700"
                      disabled={limites?.limites?.series && !limites.limites.series.puede_crear}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nueva Serie
                    </Button>
                  </div>
                  {limites?.limites?.series && limites.limites.series.limite > 0 && !limites.limites.series.puede_crear && (
                    <p className="text-sm text-amber-600 mt-2">
                      Has alcanzado el límite de {limites.limites.series.limite} serie(s) del plan {limites.plan}. Actualiza tu plan para crear más.
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  {series.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Hash className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                      <p>No hay series configuradas</p>
                      <p className="text-sm">Los presupuestos usarán numeración automática (P2024-0001)</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Código</TableHead>
                          <TableHead>Nombre</TableHead>
                          <TableHead>Prefijo</TableHead>
                          <TableHead>Último Nº</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead className="w-[100px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {series.map((serie) => (
                          <TableRow key={serie.id}>
                            <TableCell className="font-mono font-bold">{serie.codigo}</TableCell>
                            <TableCell>{serie.nombre}</TableCell>
                            <TableCell className="font-mono">{serie.prefijo}</TableCell>
                            <TableCell>{serie.ultimo_numero || 0}</TableCell>
                            <TableCell>
                              <Badge className={serie.activa ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                                {serie.activa ? 'Activa' : 'Inactiva'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openSerieDialog(serie)}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => handleDeleteSerie(serie.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900 mb-2">¿Cómo funcionan las series?</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Cada serie tiene su propia numeración correlativa</li>
                      <li>• Ejemplo: A-2024-0001, WEB-2024-0001, INST-2024-0001</li>
                      <li>• Puedes tener series por tipo de servicio, proyecto o cliente</li>
                      <li>• Al crear un presupuesto, podrás elegir qué serie usar</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Plantillas Tab */}
          {canEdit && (
            <TabsContent value="plantillas">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Plantillas de Presupuesto</CardTitle>
                      <CardDescription>
                        Crea plantillas con items predefinidos para agilizar la creación de presupuestos
                      </CardDescription>
                    </div>
                    <Button onClick={() => openPlantillaDialog()} className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Nueva Plantilla
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {plantillas.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                      <p>No hay plantillas creadas</p>
                      <p className="text-sm">Crea plantillas para reutilizar items frecuentes</p>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {plantillas.map(plantilla => (
                        <div 
                          key={plantilla.id} 
                          className="border rounded-lg p-4 hover:border-blue-200 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-medium text-slate-900">{plantilla.nombre}</h3>
                              {plantilla.descripcion && (
                                <p className="text-sm text-slate-500 mt-1">{plantilla.descripcion}</p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                                <span>{plantilla.items?.length || 0} items</span>
                                <span>•</span>
                                <span>Validez: {plantilla.validez_dias} días</span>
                                <span>•</span>
                                <span>{plantilla.forma_pago}</span>
                              </div>
                              {plantilla.items?.length > 0 && (
                                <div className="mt-3 text-sm">
                                  <span className="text-slate-500">Total estimado: </span>
                                  <span className="font-medium">
                                    {formatCurrency(plantilla.items.reduce((sum, item) => sum + (item.cantidad * item.precio_unitario), 0))}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openPlantillaDialog(plantilla)}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleDeletePlantilla(plantilla.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900 mb-2">¿Cómo usar las plantillas?</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Crea plantillas con los items que más usas</li>
                      <li>• Al crear un presupuesto, podrás seleccionar una plantilla</li>
                      <li>• Los items se cargarán automáticamente</li>
                      <li>• Puedes modificar los items después de cargarlos</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Usuarios Tab */}
          {isOwner && (
            <TabsContent value="usuarios">
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Gestión de Usuarios</CardTitle>
                  <CardDescription>
                    Añade o elimina usuarios de tu empresa
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Add User Form */}
                  <div className="flex flex-col sm:flex-row gap-4 p-4 bg-slate-50 rounded-lg">
                    <Input
                      placeholder="Email del usuario"
                      value={nuevoUsuarioEmail}
                      onChange={(e) => setNuevoUsuarioEmail(e.target.value)}
                      className="flex-1"
                      data-testid="add-user-email"
                    />
                    <Select value={nuevoUsuarioRol} onValueChange={setNuevoUsuarioRol}>
                      <SelectTrigger className="w-full sm:w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MANAGER">Manager</SelectItem>
                        <SelectItem value="VIEWER">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={handleAddUsuario} className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Añadir
                    </Button>
                  </div>

                  {/* Users Table */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Usuario</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Rol</TableHead>
                        <TableHead className="w-[70px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usuarios.map((usuario) => (
                        <TableRow key={usuario.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {usuario.nombre}
                              {usuario.rol === 'OWNER' && (
                                <Crown className="w-4 h-4 text-amber-500" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{usuario.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{usuario.rol}</Badge>
                          </TableCell>
                          <TableCell>
                            {usuario.rol !== 'OWNER' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleRemoveUsuario(usuario.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Plan Tab */}
          <TabsContent value="plan">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Tu Plan Actual</CardTitle>
                <CardDescription>
                  Gestiona tu suscripción y límites
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-4 p-6 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {planInfo[empresa.plan]?.name || empresa.plan}
                      </h3>
                      <Badge className={planInfo[empresa.plan]?.color}>
                        Activo
                      </Badge>
                    </div>
                    <p className="text-slate-500 mt-1">
                      {empresa.plan === 'FREE' && 'Plan gratuito con funcionalidades básicas'}
                      {empresa.plan === 'PRO' && 'Plan profesional con todas las funcionalidades'}
                      {empresa.plan === 'BUSINESS' && 'Plan empresarial con funcionalidades avanzadas'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-white border rounded-lg">
                    <p className="text-sm text-slate-500">Usuarios</p>
                    <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {usuarios.length} / {empresa.plan === 'FREE' ? '1' : empresa.plan === 'PRO' ? '5' : '∞'}
                    </p>
                  </div>
                  <div className="p-4 bg-white border rounded-lg">
                    <p className="text-sm text-slate-500">Presupuestos enviados</p>
                    <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {limites?.limites?.presupuestos?.usado || 0} / {limites?.limites?.presupuestos?.limite === -1 ? '∞' : limites?.limites?.presupuestos?.limite || (empresa.plan === 'FREE' ? '5' : '∞')}
                    </p>
                  </div>
                  <div className="p-4 bg-white border rounded-lg">
                    <p className="text-sm text-slate-500">Series</p>
                    <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {limites?.limites?.series?.usado || series.length} / {limites?.limites?.series?.limite === -1 ? '∞' : limites?.limites?.series?.limite || (empresa.plan === 'FREE' ? '1' : '∞')}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-white border rounded-lg">
                    <p className="text-sm text-slate-500">Exportar PDF</p>
                    <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      ✓
                    </p>
                  </div>
                  <div className="p-4 bg-white border rounded-lg">
                    <p className="text-sm text-slate-500">Tracking vistas</p>
                    <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <span className={empresa.plan === 'FREE' ? 'text-slate-400' : 'text-emerald-600'}>
                        {empresa.plan === 'FREE' ? '✗' : '✓'}
                      </span>
                    </p>
                  </div>
                  <div className="p-4 bg-white border rounded-lg">
                    <p className="text-sm text-slate-500">Duplicar presupuestos</p>
                    <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      <span className={empresa.plan === 'FREE' ? 'text-slate-400' : 'text-emerald-600'}>
                        {empresa.plan === 'FREE' ? '✗' : '✓'}
                      </span>
                    </p>
                  </div>
                </div>

                {empresa.plan !== 'BUSINESS' && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-900">¿Necesitas más?</h4>
                    <p className="text-blue-700 text-sm mt-1">
                      Actualiza tu plan para desbloquear más funcionalidades y eliminar límites.
                    </p>
                    <Button className="mt-3 bg-blue-600 hover:bg-blue-700">
                      Ver Planes
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recordatorios Tab */}
          {isOwner && (
            <TabsContent value="recordatorios">
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Recordatorios Automáticos</CardTitle>
                  <CardDescription>
                    Configura y envía recordatorios para presupuestos próximos a vencer
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="dias_antes">Días antes del vencimiento</Label>
                        <Input
                          id="dias_antes"
                          type="number"
                          min="1"
                          max="30"
                          value={recordatoriosConfig.dias_antes_vencimiento}
                          onChange={(e) => setRecordatoriosConfig({
                            ...recordatoriosConfig,
                            dias_antes_vencimiento: parseInt(e.target.value) || 3
                          })}
                        />
                        <p className="text-sm text-slate-500">
                          Se enviarán recordatorios a presupuestos que venzan en los próximos {recordatoriosConfig.dias_antes_vencimiento} días
                        </p>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Recordatorios activos</Label>
                          <p className="text-sm text-slate-500">Permitir envío de recordatorios</p>
                        </div>
                        <Switch
                          checked={recordatoriosConfig.enviar_recordatorio}
                          onCheckedChange={(checked) => setRecordatoriosConfig({
                            ...recordatoriosConfig,
                            enviar_recordatorio: checked
                          })}
                        />
                      </div>
                      
                      <Button onClick={handleSaveRecordatoriosConfig} className="w-full bg-blue-600 hover:bg-blue-700">
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Configuración
                      </Button>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-3 mb-4">
                          <Bell className="w-8 h-8 text-amber-600" />
                          <div>
                            <h3 className="font-semibold text-amber-900">Enviar Recordatorios</h3>
                            <p className="text-sm text-amber-700">
                              Envía emails a clientes con presupuestos pendientes
                            </p>
                          </div>
                        </div>
                        <Button 
                          onClick={handleEnviarRecordatorios}
                          disabled={enviandoRecordatorios}
                          className="w-full bg-amber-600 hover:bg-amber-700"
                        >
                          <Send className={`w-4 h-4 mr-2 ${enviandoRecordatorios ? 'animate-pulse' : ''}`} />
                          {enviandoRecordatorios ? 'Enviando...' : 'Enviar Recordatorios Ahora'}
                        </Button>
                        <p className="text-xs text-amber-600 mt-2 text-center">
                          Solo se envía un recordatorio por presupuesto al día
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900 mb-2">¿Cómo funcionan los recordatorios?</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Se envían a clientes con presupuestos "pendiente" próximos a vencer</li>
                      <li>• El cliente recibe un email con un resumen y enlace al presupuesto</li>
                      <li>• Solo se envía un recordatorio por presupuesto por día</li>
                      <li>• El evento queda registrado en el historial del presupuesto</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Notificaciones Tab */}
          <TabsContent value="notificaciones">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Notificaciones Push</CardTitle>
                <CardDescription>
                  Recibe notificaciones en tu navegador cuando tus clientes interactúen con los presupuestos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {pushSubscribed ? (
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    ) : (
                      <XCircle className="w-8 h-8 text-slate-300" />
                    )}
                    <div>
                      <h4 className="font-medium text-slate-900">
                        {pushSubscribed ? 'Notificaciones Activadas' : 'Notificaciones Desactivadas'}
                      </h4>
                      <p className="text-sm text-slate-500">
                        {pushSubscribed 
                          ? 'Recibirás notificaciones cuando un cliente vea, apruebe o rechace un presupuesto'
                          : 'Activa las notificaciones para estar al día'
                        }
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={pushSubscribed ? handleUnsubscribePush : handleSubscribePush}
                    disabled={pushLoading}
                    variant={pushSubscribed ? 'outline' : 'default'}
                    data-testid="toggle-push-btn"
                  >
                    {pushLoading ? 'Procesando...' : pushSubscribed ? 'Desactivar' : 'Activar'}
                  </Button>
                </div>

                {pushSubscribed && (
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium text-slate-900 mb-3">Probar Notificaciones</h4>
                    <Button 
                      onClick={handleSendTestPush}
                      variant="outline"
                      data-testid="test-push-btn"
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      Enviar Notificación de Prueba
                    </Button>
                  </div>
                )}

                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Tipos de Notificaciones</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>Presupuesto Visto:</strong> Cuando un cliente abre tu presupuesto</li>
                    <li>• <strong>Presupuesto Aprobado:</strong> Cuando un cliente acepta tu presupuesto</li>
                    <li>• <strong>Presupuesto Rechazado:</strong> Cuando un cliente rechaza tu presupuesto</li>
                    <li>• <strong>Recordatorios:</strong> Alertas sobre presupuestos próximos a vencer</li>
                  </ul>
                </div>

                {!('Notification' in window) && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-amber-800 text-sm">
                      Tu navegador no soporta notificaciones push. Prueba con Chrome, Firefox o Edge.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Exportar Tab */}
          {canEdit && empresa?.plan !== 'FREE' && (
            <TabsContent value="exportar">
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Exportar Datos</CardTitle>
                  <CardDescription>
                    Descarga tus datos en formato CSV para análisis o backup
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Export Presupuestos */}
                  <div className="p-4 border rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-slate-900">Exportar Presupuestos</h4>
                        <p className="text-sm text-slate-500">Descarga todos tus presupuestos con filtros opcionales</p>
                      </div>
                      <FileText className="w-8 h-8 text-slate-300" />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Estado</Label>
                        <Select 
                          value={exportFilters.estado} 
                          onValueChange={(val) => setExportFilters({...exportFilters, estado: val})}
                        >
                          <SelectTrigger data-testid="export-estado-select">
                            <SelectValue placeholder="Todos" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="borrador">Borrador</SelectItem>
                            <SelectItem value="pendiente">Pendiente</SelectItem>
                            <SelectItem value="aprobado">Aprobado</SelectItem>
                            <SelectItem value="rechazado">Rechazado</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Desde</Label>
                        <Input 
                          type="date"
                          value={exportFilters.fecha_desde}
                          onChange={(e) => setExportFilters({...exportFilters, fecha_desde: e.target.value})}
                          data-testid="export-fecha-desde"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Hasta</Label>
                        <Input 
                          type="date"
                          value={exportFilters.fecha_hasta}
                          onChange={(e) => setExportFilters({...exportFilters, fecha_hasta: e.target.value})}
                          data-testid="export-fecha-hasta"
                        />
                      </div>
                    </div>
                    
                    <Button 
                      onClick={handleExportPresupuestos}
                      disabled={exportLoading}
                      className="w-full"
                      data-testid="export-presupuestos-btn"
                    >
                      <Download className={`w-4 h-4 mr-2 ${exportLoading ? 'animate-bounce' : ''}`} />
                      {exportLoading ? 'Exportando...' : 'Descargar Presupuestos CSV'}
                    </Button>
                  </div>

                  {/* Export Clientes */}
                  <div className="p-4 border rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-slate-900">Exportar Clientes</h4>
                        <p className="text-sm text-slate-500">Descarga tu base de datos de clientes</p>
                      </div>
                      <Users className="w-8 h-8 text-slate-300" />
                    </div>
                    
                    <Button 
                      onClick={handleExportClientes}
                      disabled={exportLoading}
                      variant="outline"
                      className="w-full"
                      data-testid="export-clientes-btn"
                    >
                      <Download className={`w-4 h-4 mr-2 ${exportLoading ? 'animate-bounce' : ''}`} />
                      {exportLoading ? 'Exportando...' : 'Descargar Clientes CSV'}
                    </Button>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-900 mb-2">Formato del archivo</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Formato: CSV (Valores separados por punto y coma)</li>
                      <li>• Codificación: UTF-8 (compatible con Excel, Google Sheets, etc.)</li>
                      <li>• Los números utilizan formato español (coma decimal)</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Serie Dialog */}
        <Dialog open={serieDialogOpen} onOpenChange={setSerieDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingSerie ? 'Editar Serie' : 'Nueva Serie'}</DialogTitle>
              <DialogDescription>
                Configura los datos de la serie de numeración
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Código *</Label>
                  <Input
                    value={serieForm.codigo}
                    onChange={(e) => setSerieForm({ ...serieForm, codigo: e.target.value.toUpperCase() })}
                    placeholder="A, WEB, INST"
                    maxLength={10}
                    disabled={!!editingSerie}
                  />
                  <p className="text-xs text-slate-500">Identificador corto</p>
                </div>
                <div className="space-y-2">
                  <Label>Prefijo</Label>
                  <Input
                    value={serieForm.prefijo}
                    onChange={(e) => setSerieForm({ ...serieForm, prefijo: e.target.value })}
                    placeholder={`${serieForm.codigo || 'A'}-${new Date().getFullYear()}`}
                  />
                  <p className="text-xs text-slate-500">Aparece en el nº</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={serieForm.nombre}
                  onChange={(e) => setSerieForm({ ...serieForm, nombre: e.target.value })}
                  placeholder="Serie Principal, Servicios Web, etc."
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Serie Activa</Label>
                  <p className="text-xs text-slate-500">Solo series activas aparecen al crear presupuestos</p>
                </div>
                <Switch
                  checked={serieForm.activa}
                  onCheckedChange={(checked) => setSerieForm({ ...serieForm, activa: checked })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSerieDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveSerie} className="bg-blue-600 hover:bg-blue-700">
                {editingSerie ? 'Guardar Cambios' : 'Crear Serie'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Plantilla Dialog */}
        <Dialog open={plantillaDialogOpen} onOpenChange={setPlantillaDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {editingPlantilla ? 'Editar Plantilla' : 'Nueva Plantilla'}
              </DialogTitle>
              <DialogDescription>
                Crea una plantilla con items predefinidos para usar al crear presupuestos
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input
                    value={plantillaForm.nombre}
                    onChange={(e) => setPlantillaForm({ ...plantillaForm, nombre: e.target.value })}
                    placeholder="Servicio Web Básico"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Input
                    value={plantillaForm.descripcion}
                    onChange={(e) => setPlantillaForm({ ...plantillaForm, descripcion: e.target.value })}
                    placeholder="Plantilla para proyectos web"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Items</Label>
                <div className="space-y-2">
                  {plantillaForm.items.map((item, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <Input
                        placeholder="Descripción"
                        value={item.descripcion}
                        onChange={(e) => updatePlantillaItem(index, 'descripcion', e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        placeholder="Cantidad"
                        value={item.cantidad}
                        onChange={(e) => updatePlantillaItem(index, 'cantidad', parseFloat(e.target.value) || 1)}
                        className="w-20"
                      />
                      <Input
                        type="number"
                        placeholder="Precio"
                        value={item.precio_unitario}
                        onChange={(e) => updatePlantillaItem(index, 'precio_unitario', parseFloat(e.target.value) || 0)}
                        className="w-24"
                      />
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => removePlantillaItem(index)}
                        disabled={plantillaForm.items.length === 1}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addPlantillaItem}>
                    <Plus className="w-4 h-4 mr-1" />
                    Añadir Item
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Validez (días)</Label>
                  <Input
                    type="number"
                    value={plantillaForm.validez_dias}
                    onChange={(e) => setPlantillaForm({ ...plantillaForm, validez_dias: parseInt(e.target.value) || 30 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Forma de Pago</Label>
                  <Select 
                    value={plantillaForm.forma_pago} 
                    onValueChange={(val) => setPlantillaForm({ ...plantillaForm, forma_pago: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Transferencia bancaria">Transferencia bancaria</SelectItem>
                      <SelectItem value="Efectivo">Efectivo</SelectItem>
                      <SelectItem value="Tarjeta de crédito">Tarjeta de crédito</SelectItem>
                      <SelectItem value="50% inicio, 50% entrega">50% inicio, 50% entrega</SelectItem>
                      <SelectItem value="Pago a 30 días">Pago a 30 días</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notas predefinidas</Label>
                <Textarea
                  value={plantillaForm.notas}
                  onChange={(e) => setPlantillaForm({ ...plantillaForm, notas: e.target.value })}
                  placeholder="Notas que se añadirán automáticamente al presupuesto"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPlantillaDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSavePlantilla} className="bg-blue-600 hover:bg-blue-700">
                {editingPlantilla ? 'Guardar Cambios' : 'Crear Plantilla'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
